import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  UsersIcon, 
  BuildingStorefrontIcon, 
  ChartBarIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  GlobeAltIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import apiService from '../../services/api';
import { DateDisplay } from '../../components/Forms';

const AdminDashboard: React.FC = () => {
  // Fetch admin-specific data
  const { data: vendorStats, isLoading: vendorStatsLoading } = useQuery({
    queryKey: ['vendorStats'],
    queryFn: () => apiService.getVendorStats(),
    enabled: true
  });

  const { data: vendorsData } = useQuery({
    queryKey: ['vendors', 'overview'],
    queryFn: () => apiService.getVendors(1, 5),
    enabled: true
  });

  const stats = vendorStats || {
    total: 0,
    active: 0,
    inactive: 0,
    expiredSubscriptions: 0,
    planStats: []
  };

  const recentVendors = vendorsData?.data || [];

  const TONE: Record<string, string> = {
    primary: 'bg-primary-50 text-primary-600',
    green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600',
    amber: 'bg-amber-50 text-amber-600',
  };

  const StatCard = ({ title, value, icon: Icon, tone = 'primary', subtitle }: any) => (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-gray-500">{title}</p>
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${TONE[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <div className="mt-2 text-2xl font-semibold text-gray-900">
        {vendorStatsLoading ? <div className="h-8 w-16 animate-pulse rounded bg-gray-100" /> : value}
      </div>
      {subtitle && <p className="mt-1 text-xs text-gray-500">{subtitle}</p>}
    </div>
  );

  const QuickActionCard = ({ title, description, icon: Icon, href }: any) => (
    <Link
      to={href}
      className="group flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 transition-colors hover:border-primary-200 hover:bg-primary-50/40"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-500 transition-colors group-hover:bg-primary-100 group-hover:text-primary-700">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="font-medium text-gray-900">{title}</h3>
        <p className="truncate text-xs text-gray-500">{description}</p>
      </div>
      <ChevronLeftIcon className="h-4 w-4 shrink-0 text-gray-300 transition-colors group-hover:text-primary-500" />
    </Link>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">لوحة تحكم المدير</h1>
          <p className="mt-1 text-sm text-gray-500">نظرة عامة على النظام وإدارة البائعين</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="btn-primary py-2">
            <UsersIcon className="h-5 w-5" />
            إضافة بائع جديد
          </button>
          <button className="btn-secondary py-2">
            <ChartBarIcon className="h-5 w-5" />
            تقرير شامل
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="إجمالي البائعين"
          value={stats.total}
          icon={BuildingStorefrontIcon}
        />
        <StatCard
          title="البائعون النشطون"
          value={stats.active}
          icon={CheckCircleIcon}
          tone="green"
          subtitle={`${stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : 0}% من الإجمالي`}
        />
        <StatCard
          title="الاشتراكات المنتهية"
          value={stats.expiredSubscriptions}
          icon={ExclamationTriangleIcon}
          tone="red"
        />
        <StatCard
          title="البائعون غير النشطين"
          value={stats.inactive}
          icon={ClockIcon}
          tone="amber"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-1">
          <h2 className="mb-3 text-sm font-semibold text-gray-500">إجراءات سريعة</h2>
          <div className="space-y-3">
            <QuickActionCard
              title="إدارة البائعين"
              description="إضافة أو تعديل أو حذف البائعين"
              icon={UsersIcon}
              href="/vendors"
            />
            <QuickActionCard
              title="التقارير المالية"
              description="عرض الإحصائيات المالية للنظام"
              icon={CurrencyDollarIcon}
              href="/financial-reports"
            />
            <QuickActionCard
              title="إعدادات النظام"
              description="تكوين إعدادات النظام العامة"
              icon={GlobeAltIcon}
              href="/system-settings"
            />
          </div>
        </div>

        {/* Recent Vendors */}
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-6 py-4">
              <h2 className="font-semibold text-gray-900">أحدث البائعين</h2>
            </div>
            <div className="p-6">
              {recentVendors.length === 0 ? (
                <div className="text-center py-8">
                  <BuildingStorefrontIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">لا يوجد بائعون مسجلون</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {recentVendors.map((vendor: any) => (
                    <div key={vendor._id} className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${vendor.isActive ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <div className="min-w-0">
                          <h3 className="font-medium text-gray-900">{vendor.name}</h3>
                          <p className="text-sm text-gray-500">{vendor.companyName}</p>
                        </div>
                      </div>
                      <div className="text-left">
                        <span className={`badge ${
                          vendor.subscription.plan === 'basic' || !vendor.subscription.plan
                            ? 'bg-gray-100 text-gray-600'
                            : 'bg-primary-50 text-primary-700'
                        }`}>
                          {vendor.subscription.plan === 'premium' ? 'مميز' :
                           vendor.subscription.plan === 'enterprise' ? 'مؤسسات' : 'أساسي'}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          ينتهي في <DateDisplay value={vendor.subscription.endDate} className="inline-flex" />
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Plan Statistics */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-6 py-4">
          <h2 className="font-semibold text-gray-900">إحصائيات الخطط</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.planStats?.map((plan: any) => (
              <div key={plan._id} className="text-center">
                <div className="text-2xl font-bold text-gray-900">{plan.count}</div>
                <div className="text-sm text-gray-500">
                  {plan._id === 'premium' ? 'خطة مميزة' :
                   plan._id === 'enterprise' ? 'خطة مؤسسات' : 'خطة أساسية'}
                </div>
              </div>
            )) || (
              <>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">0</div>
                  <div className="text-sm text-gray-500">خطة أساسية</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">0</div>
                  <div className="text-sm text-gray-500">خطة مميزة</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">0</div>
                  <div className="text-sm text-gray-500">خطة مؤسسات</div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 